import React, { FC } from 'react';
import clsx from 'clsx';

import { RichText, Link } from '@marriott/mi-ui-library';

import { constants, TRACKING_CONSTANTS } from '../../modules';
import { addClassToBlankTargetLinks } from '../../modules/utils/helper';
import { CustomTableCell } from './CustomTableCell/CustomTableCell';
import {
  HeaderInlineRowProps,
  HeaderEliteRowProps,
  HeaderSubtitleRowProps,
  TableHeaderGroupProps,
  BodyInlineRowProps,
  BodyContentRowProps,
  TableBodyGroupProps,
  AccountTableViewProps,
} from './AccountTable.types';
import { StyledAccountTable } from './AccountTable.styles';

const HeaderInlineRow: FC<HeaderInlineRowProps> = ({ mainTitle, headerSubItemsLength, rowIndex }) => (
  <tr className={clsx('row-header-inline', rowIndex !== 0 && 'top-border')}>
    <CustomTableCell
      text={mainTitle}
      classname="header-cell row-header"
      customTextClass="t-subtitle-m right-border__column row-header px-3 px-md-4 py-4"
      id="account-table-main-title-inline"
      colSpan={headerSubItemsLength}
    />
  </tr>
);

const HeaderEliteRow: FC<HeaderEliteRowProps> = ({ headerData, rowIndex, variation }) => (
  <tr className={clsx(rowIndex !== 0 ? 'top-border' : '', 'elite-header')}>
    <CustomTableCell
      rowSpan={2}
      text={headerData?.mainTitle}
      classname={clsx(
        'header-cell row-header',
        variation === constants?.DEFAULT
          ? 'row-header-default right-border__column'
          : variation === constants?.HIDE_ROW_HEADER
          ? 'row-header-hidden right-border__column'
          : 'right-border'
      )}
      customTextClass="t-subtitle-m row-header px-3 px-md-4 py-4"
      id="account-table-main-title-default"
    />
    {headerData?.headerSubItems?.map(
      (headerRowData, colIndex) =>
        (!!headerRowData?.subtitle || !!headerRowData?.title) && (
          <CustomTableCell
            text={headerRowData?.title || headerRowData?.subtitle}
            fontIconClass={`${
              headerRowData?.title
                ? headerRowData?.titleBackGroundColor
                  ? 't-label-inverse-xs'
                  : 't-label-xs'
                : 't-label-alt-xs'
            }`}
            classname={`${colIndex + 1 !== headerData?.headerSubItems?.length ? 'right-border' : ''} header-cell`}
            colIndex={colIndex}
            rowIndex={rowIndex}
            backGroundColor={headerRowData?.title ? headerRowData?.titleBackGroundColor : ''}
            rowSpan={headerRowData?.subtitle && headerRowData?.title ? 1 : 2}
          />
        )
    )}
  </tr>
);

const HeaderSubtitleRow: FC<HeaderSubtitleRowProps> = ({ headerData }) => (
  <tr>
    {headerData?.headerSubItems?.map(
      (headerRowData, subRowIndex) =>
        headerRowData?.subtitle &&
        headerRowData?.title && (
          <CustomTableCell
            text={headerRowData?.subtitle}
            classname={clsx(
              't-label-alt-xs p-2 align-content-between',
              subRowIndex + 1 !== headerData?.headerSubItems?.length && 'right-border',
              headerRowData?.title && 'top-border'
            )}
            customTextClass="header-cell align-items-center justify-content-around justify-content-md-center text-center d-flex"
            id="member-benefit-header"
          />
        )
    )}
  </tr>
);

const TableHeaderGroup: FC<TableHeaderGroupProps> = ({ headerData, rowIndex, variation, theadRef }) => (
  <thead ref={theadRef} className="table-header" data-testid="table-header">
    {variation === constants?.DEFAULT && (
      <HeaderInlineRow
        mainTitle={headerData?.mainTitle}
        headerSubItemsLength={headerData?.headerSubItems?.length}
        rowIndex={rowIndex}
      />
    )}
    <HeaderEliteRow headerData={headerData} rowIndex={rowIndex} variation={variation} />
    <HeaderSubtitleRow headerData={headerData} />
  </thead>
);

const BodyInlineRow: FC<BodyInlineRowProps> = ({ rowData, domRendered }) => (
  <tr className="row-header-inline top-border right-border__column">
    <CustomTableCell colSpan={rowData?.rowSubItems?.length}>
      <div className="py-3 px-3 px-md-4">
        <RichText
          text={domRendered ? addClassToBlankTargetLinks(rowData?.rowTitle || '') : rowData?.rowTitle || ''}
          customClass="header-cell mb-2 t-subtitle-m"
          componentId="body-row-header"
        />
        <RichText
          text={rowData?.rowSubTitle || ''}
          customClass="header-cell t-font-alt-s"
          componentId="body-row-sub-header"
        />
        {rowData?.rowCtaCopy && rowData?.rowCtaLink && (
          <Link
            linkType={rowData?.linkType || TRACKING_CONSTANTS?.INTERNAL}
            linkClassName={`pt-2 t-font-s ${
              rowData?.linkType === TRACKING_CONSTANTS?.INTERNAL
                ? 'm-link-tertiary-button'
                : 'm-link-tertiary-button-external'
            }`}
            target={rowData?.linkType === TRACKING_CONSTANTS?.INTERNAL ? '_self' : '_blank'}
            text={rowData?.rowCtaCopy || ''}
            linkHref={rowData?.rowCtaLink || ''}
            trackingProperties={rowData?.trackingProperties}
          />
        )}
      </div>
    </CustomTableCell>
  </tr>
);

const BodyContentRow: FC<BodyContentRowProps> = ({ rowData, rowIndex, variation, domRendered }) => (
  <tr className="top-border">
    <CustomTableCell
      classname={clsx(
        variation === constants?.DEFAULT
          ? 'row-header-default right-border__column'
          : variation === constants?.HIDE_ROW_HEADER
          ? 'row-header-hidden right-border__column'
          : 'right-border',
        'row-header'
      )}
    >
      <div className="row-header py-3 px-3 px-md-4">
        <RichText
          text={domRendered ? addClassToBlankTargetLinks(rowData?.rowTitle || '') : rowData?.rowTitle || ''}
          customClass="mb-2 t-subtitle-m"
          componentId="body-row-header"
        />
        <RichText text={rowData?.rowSubTitle || ''} customClass="t-font-alt-s" componentId="body-row-sub-header" />
        {rowData?.rowCtaCopy && rowData?.rowCtaLink && (
          <Link
            linkType={rowData?.linkType || TRACKING_CONSTANTS?.INTERNAL}
            linkClassName={`pt-2 t-font-s ${
              rowData?.linkType === TRACKING_CONSTANTS?.INTERNAL
                ? 'm-link-tertiary-button'
                : 'm-link-tertiary-button-external'
            }`}
            target={rowData?.linkType === TRACKING_CONSTANTS?.INTERNAL ? '_self' : '_blank'}
            text={rowData?.rowCtaCopy || ''}
            linkHref={rowData?.rowCtaLink || ''}
            trackingProperties={rowData?.trackingProperties}
          />
        )}
      </div>
    </CustomTableCell>

    {rowData?.rowSubItems?.map((rowSubItems, colIndex) => (
      <>
        {rowSubItems?.variation === constants?.TEXT ? (
          <CustomTableCell
            text={rowSubItems?.subRowText}
            classname={colIndex + 1 !== rowData?.rowSubItems?.length ? 'right-border' : ''}
            customTextClass="t-font-xs d-flex align-items-center justify-content-center text-center px-2 py-3"
            id={`body-row-header-${colIndex}${rowIndex}`}
          />
        ) : rowSubItems?.variation === constants?.FONTICON ? (
          <CustomTableCell
            fontIconClass={rowSubItems?.fontIconSubRowComplex}
            backGroundColor={rowSubItems?.fontIconColor}
            classname={clsx(
              'align-items-center justify-content-center text-center px-2 py-3',
              colIndex + 1 !== rowData?.rowSubItems?.length && 'right-border'
            )}
            id={`chart-icon-${colIndex}${rowIndex}`}
            isIcon={rowSubItems?.fontIconSubRowComplex}
          />
        ) : null}
      </>
    ))}
  </tr>
);

const TableBodyGroup: FC<TableBodyGroupProps> = ({ rowData, rowIndex, variation, domRendered }) => (
  <tbody>
    {variation === constants?.DEFAULT && <BodyInlineRow rowData={rowData} domRendered={domRendered} />}
    <BodyContentRow rowData={rowData} rowIndex={rowIndex} variation={variation} domRendered={domRendered} />
  </tbody>
);

export const AccountTableView: FC<AccountTableViewProps> = ({
  id,
  variation,
  headerTitleItems,
  rowItems,
  disclaimerText,
  domRendered,
  theadRef,
  tableRef,
  lateCheckoutDisclaimer,
}) => (
  <div
    id={id}
    data-testid="account-AccountTable"
    data-component-name="o-account-AccountTable"
    className="m-container-fullbleed py-3 py-md-4"
  >
    <StyledAccountTable className="container-md px-0">
      <div className="color-scheme1 table-container">
        <table ref={tableRef}>
          {headerTitleItems?.map((headerData, rowIndex) => (
            <TableHeaderGroup
              key={rowIndex}
              headerData={headerData}
              rowIndex={rowIndex}
              variation={variation}
              theadRef={theadRef}
            />
          ))}
          {rowItems?.map((rowData, rowIndex) => (
            <TableBodyGroup
              key={rowIndex}
              rowData={rowData}
              rowIndex={rowIndex}
              variation={variation}
              domRendered={domRendered}
            />
          ))}
        </table>
      </div>
      {lateCheckoutDisclaimer && (
        <RichText
          text={lateCheckoutDisclaimer || ''}
          customClass="disclaimerText t-font-s mt-4 px-3 px-md-1"
          componentId="disclaimer"
        />
      )}
      {disclaimerText && (
        <RichText
          text={domRendered ? addClassToBlankTargetLinks(disclaimerText || '') : disclaimerText || ''}
          customClass="disclaimerText t-font-s mt-4 px-3 px-md-0"
          componentId="disclaimer"
        />
      )}
    </StyledAccountTable>
  </div>
);
